import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Link } from 'react-router-dom';
import { User, Clock, GripVertical, Activity } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { PipelineApplication } from '@/lib/api';

interface KanbanCardProps {
  application: PipelineApplication;
  isDragging?: boolean;
  /** Compact one-line mode: drag handle + name + days-in-stage only. */
  collapsed?: boolean;
}

export function KanbanCard({ application, isDragging, collapsed }: KanbanCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({
    id: application.id,
    data: {
      type: 'application',
      application,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const candidate = application.candidate;
  const isBeingDragged = isDragging || isSortableDragging;

  if (collapsed) {
    return (
      <div
        ref={setNodeRef}
        style={style}
        className={cn(
          'flex items-center gap-2 rounded-lg border border-white/10 bg-bg-tertiary px-2 py-1.5 shadow-raised',
          'hover:border-white/20 hover:shadow-floating transition-all duration-150',
          isBeingDragged && 'border-primary-400 opacity-90',
          'cursor-grab active:cursor-grabbing',
        )}
      >
        <button
          {...attributes}
          {...listeners}
          className="flex-shrink-0 text-text-tertiary hover:text-text-secondary cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <Link
          to={`/candidates/${candidate.id}`}
          onClick={(e) => e.stopPropagation()}
          className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary hover:text-emerald-500"
        >
          {candidate.firstName} {candidate.lastName}
        </Link>
        <span className="flex flex-shrink-0 items-center gap-1 text-[11px] text-text-tertiary">
          <Clock className="h-3 w-3" />
          {application.daysInStage === 0 ? 'Today' : `${application.daysInStage}d`}
        </span>
      </div>
    );
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'bg-bg-tertiary rounded-lg border border-white/10 p-3 shadow-raised surface-sheen',
        'hover:shadow-floating hover:border-white/20 transition-all duration-150',
        isBeingDragged && 'shadow-floating border-primary-400 opacity-90 rotate-2',
        'cursor-grab active:cursor-grabbing',
      )}
    >
      <div className="flex items-start gap-2">
        {/* Drag Handle */}
        <button
          {...attributes}
          {...listeners}
          className="flex-shrink-0 mt-0.5 text-text-tertiary hover:text-text-secondary cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="h-4 w-4" />
        </button>

        {/* Card Content */}
        <div className="flex-1 min-w-0">
          {/* Candidate Name */}
          <Link
            to={`/candidates/${candidate.id}`}
            className="font-medium text-text-primary hover:text-emerald-500 block truncate"
            onClick={(e) => e.stopPropagation()}
          >
            {candidate.firstName} {candidate.lastName}
          </Link>

          {/* Job Title / Employer */}
          {candidate.currentJobTitle && (
            <div className="text-sm text-text-tertiary truncate mt-0.5">
              {candidate.currentJobTitle}
              {candidate.currentEmployer && ` at ${candidate.currentEmployer}`}
            </div>
          )}

          {/* Meta Info */}
          <div className="flex items-center gap-3 mt-2 text-xs text-text-tertiary">
            {/* Days in Stage */}
            <div className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              <span>
                {application.daysInStage === 0
                  ? 'Today'
                  : application.daysInStage === 1
                    ? '1 day'
                    : `${application.daysInStage} days`}
              </span>
            </div>

            {/* Location */}
            {candidate.currentLocation && (
              <div className="flex items-center gap-1 truncate">
                <User className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{candidate.currentLocation}</span>
              </div>
            )}
          </div>

          {/* Last Activity */}
          {application.lastActivity && (
            <div className="flex items-center gap-1 mt-1.5 text-xs text-text-tertiary">
              <Activity className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">{application.lastActivity}</span>
            </div>
          )}

          {/* Skills Preview */}
          {candidate.skills && candidate.skills.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {candidate.skills.slice(0, 3).map((skill) => (
                <span
                  key={skill}
                  className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-white/10 text-text-secondary"
                >
                  {skill}
                </span>
              ))}
              {candidate.skills.length > 3 && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs text-text-tertiary">
                  +{candidate.skills.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Current Stage Badge (only shown when disqualified) */}
      {application.currentStage === 'disqualified' && application.disqualificationReason && (
        <div className="mt-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 px-2 py-1 rounded">
          {application.disqualificationReason}
        </div>
      )}
    </div>
  );
}

// Skeleton loader for KanbanCard
export function KanbanCardSkeleton() {
  return (
    <div className="bg-bg-tertiary rounded-lg border border-white/10 p-3 shadow-sm animate-pulse">
      <div className="flex items-start gap-2">
        <div className="w-4 h-4 bg-white/10 rounded flex-shrink-0" />
        <div className="flex-1">
          <div className="h-5 bg-white/10 rounded w-3/4" />
          <div className="h-4 bg-white/10 rounded w-1/2 mt-1" />
          <div className="flex gap-3 mt-2">
            <div className="h-3 bg-white/10 rounded w-12" />
            <div className="h-3 bg-white/10 rounded w-16" />
          </div>
        </div>
      </div>
    </div>
  );
}
